import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { getCached, setCached } from '../lib/hookCache'

const CACHE_SCOPE = 'sessions'

// Lists all sessions for the current user, newest first, with derived
// totals computed client-side (exercise count, non-warmup set count,
// total volume, cardio block count + duration). One round-trip pulls the
// nested logs/sets/cardios; for a personal app this stays small.
export function useSessions() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [sessions, setSessions] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(!!cached)

  const fetchSessions = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from('sessions')
      .select(`
        id, date, session_type, duration_min, body_weight_kg, energy_rating, notes, created_at,
        exercise_logs(id, sets(weight_kg, reps, is_warmup)),
        cardio_logs(id, duration_min, calories_estimated)
      `)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    const decorated = (data || []).map((s) => {
      const logs = s.exercise_logs || []
      const cardios = s.cardio_logs || []
      let setCount = 0
      let totalVolume = 0
      for (const l of logs) {
        for (const set of (l.sets || [])) {
          if (set.is_warmup) continue
          setCount += 1
          totalVolume += Number(set.weight_kg || 0) * Number(set.reps || 0)
        }
      }
      const cardioDurationMin = cardios.reduce((sum, c) => sum + Number(c.duration_min || 0), 0)
      return {
        ...s,
        exerciseCount: logs.length,
        setCount,
        totalVolume,
        cardioCount: cardios.length,
        cardioDurationMin,
      }
    })
    setSessions(decorated)
    setCached(CACHE_SCOPE, user.id, decorated)
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetchSessions() }, [fetchSessions])
  usePhoenixRefresh(fetchSessions)

  return { sessions, loading, error, refetch: fetchSessions }
}
